import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import DetectionPage from "./pages/DetectionPage";
import HistoryPage from "./pages/HistoryPage";
import SettingsPage from "./pages/SettingsPage";
import TrainingPage from "./pages/TrainingPage";
import KioskPage from "./pages/KioskPage";
import MembersPage from "./pages/MembersPage";
import CashierPage from "./pages/CashierPage";
import UsersPage from "./pages/UsersPage";
import PermissionsPage from "./pages/PermissionsPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import VehiclesPage from "./pages/VehiclesPage";
import CamerasPage from "./pages/CamerasPage";
import ConnectorsPage from "./pages/ConnectorsPage";
import OCRReviewPage from "./pages/OCRReviewPage";
import MemberPaymentsPage from "./pages/MemberPaymentsPage";
import SitesPage from "./pages/SitesPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ImpersonationBanner from "./components/ImpersonationBanner";
import ThemeToggle from "./components/ThemeToggle";
import InstallAppPrompt from "./components/InstallAppPrompt";
import FlowIA from "./components/FlowIA";
import AppNavigation from "./components/AppNavigation";
import { Toaster } from "./components/ui/sonner";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><div className="text-slate-400">Cargando...</div></div>;
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" />;
  return (
    <div className="min-h-screen bg-slate-900">
      <AppNavigation />
      {children}
    </div>
  );
}

/** Flow IA helper — visible only when authenticated, never on Kiosk (no chrome). */
function AuthenticatedExtras() {
  const { user } = useAuth();
  if (!user) return null;
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/kiosk')) return null;
  return <FlowIA />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Toaster richColors position="top-right" />
          <ImpersonationBanner />
          <ThemeToggle />
          <InstallAppPrompt />
          <AuthenticatedExtras />
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/detection" element={<ProtectedRoute allowedRoles={['super_admin','admin','operator']}><DetectionPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><SettingsPage /></ProtectedRoute>} />
          <Route path="/training" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><TrainingPage /></ProtectedRoute>} />
          <Route path="/kiosk" element={<ProtectedRoute allowedRoles={['super_admin','admin','operator','cashier']}><KioskPage /></ProtectedRoute>} />
          <Route path="/members" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><MembersPage /></ProtectedRoute>} />
          <Route path="/member-payments" element={<ProtectedRoute allowedRoles={['super_admin','admin','cashier']}><MemberPaymentsPage /></ProtectedRoute>} />
          <Route path="/vehicles" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><VehiclesPage /></ProtectedRoute>} />
          <Route path="/cashier" element={<ProtectedRoute allowedRoles={['super_admin','admin','cashier']}><CashierPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><UsersPage /></ProtectedRoute>} />
          <Route path="/permissions" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><PermissionsPage /></ProtectedRoute>} />
          <Route path="/cameras" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><CamerasPage /></ProtectedRoute>} />
          <Route path="/connectors" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><ConnectorsPage /></ProtectedRoute>} />
          <Route path="/ocr-review" element={<ProtectedRoute allowedRoles={['super_admin','admin','operator']}><OCRReviewPage /></ProtectedRoute>} />
          <Route path="/super-admin" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminPage /></ProtectedRoute>} />
          <Route path="/sites" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><SitesPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute allowedRoles={['super_admin','admin']}><AnalyticsPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
